import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ArrowDownward, ArrowUpward, DeleteOutline } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import HourPresetInput from "../../common/hour-preset-input";
import ToggleSwitch from "../../common/toggle-switch";
import { getTimezoneOptions } from "../../common/iana-timezones";
import { INSTAGRAM_ACCOUNT_DETAIL, INSTAGRAM_ACCOUNT_ICE_BREAKERS } from "../../config/urls";
import { DmPreview, ReplyEditor } from "./RuleMessage";
import { Section, errorMessage } from "./shared";

// Phase 2 tabs of the account page: Ice breakers, Business hours, Routing.
// Each tab saves on its own; business hours and routing live in account.settings.

const TIMEZONES = getTimezoneOptions();
const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];
const DEFAULT_SPAN = ["09:00", "18:00"];
const shrink = { InputLabelProps: { shrink: true } };

const useSave = (account, request, notify, onSaved) => {
  const [busy, setBusy] = useState(false);
  const save = async (settings, message) => {
    setBusy(true);
    try {
      onSaved(await request("patch", INSTAGRAM_ACCOUNT_DETAIL(account.id), { settings }));
      notify(message);
    } catch (error) {
      notify(errorMessage(error, "Could not save the settings."), "error");
    } finally {
      setBusy(false);
    }
  };
  return [busy, save];
};

// Save button pinned under a tab's cards.
const SaveBar = ({ busy, title, onClick }) => (
  <Box display="flex" justifyContent="flex-end">
    <ButtonOne title={busy ? "Saving…" : title} disabled={busy} onClick={onClick} />
  </Box>
);

// Label + description on the left, control on the right.
const SettingRow = ({ title, hint, children }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" gap={3}>
    <Box minWidth={0}>
      <Typography fontWeight={600} fontSize="0.95rem">{title}</Typography>
      {hint && <Typography variant="body2" color="text.secondary">{hint}</Typography>}
    </Box>
    <Box flexShrink={0}>{children}</Box>
  </Box>
);

// ── Ice breakers ──────────────────────────────────────────────────────────────
const toItem = (rule) => ({
  id: rule.id,
  question: rule.keywords[0] || "",
  reply_text: rule.reply_text,
  buttons: rule.buttons,
  quick_replies: rule.quick_replies,
  action_type: rule.action_type,
});

export const IceBreakersTab = ({ account, request, notify, canManage }) => {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    request("get", INSTAGRAM_ACCOUNT_ICE_BREAKERS(account.id))
      .then((data) => setItems(data.ice_breakers.map(toItem)))
      .catch((error) => {
        notify(errorMessage(error, "Could not load ice breakers."), "error");
        setItems([]);
      });
  }, [account.id, request, notify]);

  if (!items) return <Box display="flex" justifyContent="center" py={6}><CircularProgress size={26} /></Box>;

  const update = (i, fields) => setItems(items.map((item, j) => (j === i ? { ...item, ...fields } : item)));
  const move = (i, by) => {
    const next = [...items];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    setItems(next);
    setSelected(i + by);
  };
  const remove = (i) => {
    setItems(items.filter((_, j) => j !== i));
    setSelected(0);
  };
  const add = () => {
    setItems([...items, { question: "", reply_text: "", buttons: [], quick_replies: [], action_type: "reply" }]);
    setSelected(items.length);
  };

  const publish = async () => {
    setBusy(true);
    try {
      const data = await request("put", INSTAGRAM_ACCOUNT_ICE_BREAKERS(account.id), { ice_breakers: items });
      setItems(data.ice_breakers.map(toItem));
      notify(data.published ? `Published ${data.published} ice breaker${data.published === 1 ? "" : "s"} to Instagram.` : "Ice breakers removed from Instagram.");
    } catch (error) {
      notify(errorMessage(error, "Could not publish the ice breakers."), "error");
    } finally {
      setBusy(false);
    }
  };

  const current = items[selected];
  return (
    <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "minmax(0, 1fr) 340px" }} gap={3} alignItems="start">
      <Box display="flex" flexDirection="column" gap={3} minWidth={0}>
        <Section
          title="Ice breakers"
          hint={`Up to 4 questions people can tap when they first open a chat with @${account.username}. Click a question to edit its reply.`}
          action={canManage && items.length < 4 && (
            <ButtonOne variant="outlined" startIcon={<Add />} title="Add question" onClick={add} />
          )}
        >
          {items.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography fontWeight={600}>No ice breakers yet</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Add common questions like “What are your prices?” so people can start a chat in one tap.
              </Typography>
              {canManage && <ButtonOne startIcon={<Add />} title="Add first question" onClick={add} />}
            </Box>
          )}

          {items.map((item, i) => {
            const open = i === selected;
            return (
              <Box key={item.id || `new${i}`} onClick={() => setSelected(i)}
                sx={{ border: "1px solid", borderColor: open ? "#7b3fe4" : "#eceff1", borderRadius: "12px",
                  p: 2, bgcolor: open ? "#fcfaff" : "#fff", cursor: open ? "default" : "pointer" }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: "#f1ebfd", color: "#7b3fe4",
                    fontWeight: 700, fontSize: "0.8rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {i + 1}
                  </Box>
                  {open ? (
                    <TextField size="small" label="Question" value={item.question} sx={{ flex: 1 }} {...shrink}
                      placeholder="e.g. What are your prices?" inputProps={{ maxLength: 80 }} disabled={!canManage}
                      helperText={`${item.question.length}/80`}
                      onChange={(e) => update(i, { question: e.target.value })} />
                  ) : (
                    <Box flex={1} minWidth={0}>
                      <Typography fontWeight={600} noWrap>{item.question || "Untitled question"}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.action_type === "assign" ? "Hands over to an agent" : item.reply_text || "No reply yet"}
                      </Typography>
                    </Box>
                  )}
                  {canManage && (
                    <Box display="flex" flexShrink={0} alignSelf={open ? "flex-start" : "center"}>
                      <IconButton size="small" title="Move up" disabled={i === 0}
                        onClick={(e) => { e.stopPropagation(); move(i, -1); }}><ArrowUpward fontSize="small" /></IconButton>
                      <IconButton size="small" title="Move down" disabled={i === items.length - 1}
                        onClick={(e) => { e.stopPropagation(); move(i, 1); }}><ArrowDownward fontSize="small" /></IconButton>
                      <IconButton size="small" title="Remove"
                        onClick={(e) => { e.stopPropagation(); remove(i); }}><DeleteOutline fontSize="small" /></IconButton>
                    </Box>
                  )}
                </Box>
                {open && (
                  <Box display="flex" flexDirection="column" gap={2.5} mt={2.5} pl={{ sm: 4.75 }}>
                    <TextField select size="small" label="When tapped" value={item.action_type} fullWidth {...shrink}
                      disabled={!canManage} onChange={(e) => update(i, { action_type: e.target.value })}>
                      <MenuItem value="reply">Send a reply</MenuItem>
                      <MenuItem value="assign">Hand over to an agent (optional message first)</MenuItem>
                    </TextField>
                    <ReplyEditor value={item} onChange={(fields) => update(i, fields)}
                      label={item.action_type === "assign" ? "Message before handover (optional)" : "Reply message"} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Section>
        {canManage && <SaveBar busy={busy} title="Save & publish to Instagram" onClick={publish} />}
      </Box>

      <Box sx={{ position: { lg: "sticky" }, top: 16 }}>
        <Section title="Preview" hint="What people see after tapping the selected question.">
          <DmPreview account={account} inbound={current?.question || "Your question"} rule={current}
            width={260} height={420} />
        </Section>
      </Box>
    </Box>
  );
};

// ── Business hours ────────────────────────────────────────────────────────────
export const BusinessHoursTab = ({ account, request, notify, onSaved, canManage }) => {
  const settings = account.settings || {};
  const [timezone, setTimezone] = useState(settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [hours, setHours] = useState(settings.hours || {});
  const [holidays, setHolidays] = useState(settings.holidays || []);
  const [holiday, setHoliday] = useState("");
  const [busy, save] = useSave(account, request, notify, onSaved);

  const span = (day) => (hours[day] || [])[0];
  const setSpan = (day, value) => setHours({ ...hours, [day]: value ? [value] : [] });
  const configured = Object.values(hours).some((spans) => spans?.length);
  const copyMondayToWeekdays = () => {
    const monday = span("mon") || DEFAULT_SPAN;
    setHours({ ...hours, ...Object.fromEntries(["mon", "tue", "wed", "thu", "fri"].map((d) => [d, [monday]])) });
  };

  return (
    <Box display="flex" flexDirection="column" gap={3} maxWidth={860}>
      {!configured && (
        <Alert severity="info">No business hours set, so the “Outside business hours” automation never runs.</Alert>
      )}

      <Section title="Timezone" hint="Hours below are in this timezone.">
        <Autocomplete
          size="small"
          options={TIMEZONES}
          value={TIMEZONES.find((t) => t.value === timezone) || null}
          onChange={(_, option) => option && setTimezone(option.value)}
          groupBy={(o) => o.region}
          renderInput={(params) => <TextField {...params} label="Timezone" {...shrink} />}
          disabled={!canManage}
          sx={{ maxWidth: 420 }}
        />
      </Section>

      <Section
        title="Weekly hours"
        hint="Outside these hours, the out-of-hours automation replies instead of the default reply."
        action={canManage && (
          <ButtonOne variant="outlined" title="Copy Monday to weekdays" onClick={copyMondayToWeekdays} />
        )}
      >
        <Box display="flex" flexDirection="column">
          {DAYS.map(([day, label]) => {
            const value = span(day);
            return (
              <Box key={day} display="flex" alignItems="center" gap={2} py={1.25} borderBottom="1px solid #f2f4f5"
                flexWrap="wrap">
                <Box width={130} display="flex" alignItems="center" gap={1.5}>
                  <ToggleSwitch checked={Boolean(value)}
                    onChange={() => canManage && setSpan(day, value ? null : DEFAULT_SPAN)} />
                  <Typography variant="body2" fontWeight={600}>{label}</Typography>
                </Box>
                {value ? (
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <TextField type="time" size="small" value={value[0]} disabled={!canManage} sx={{ width: 140 }}
                      onChange={(e) => setSpan(day, [e.target.value, value[1]])} />
                    <Typography variant="body2" color="text.secondary">to</Typography>
                    <TextField type="time" size="small" value={value[1]} disabled={!canManage} sx={{ width: 140 }}
                      onChange={(e) => setSpan(day, [value[0], e.target.value])} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">Closed</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Section>

      <Section title="Holidays" hint="Closed all day on these dates.">
        {holidays.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {holidays.map((d) => (
              <Chip key={d} label={new Date(`${d}T00:00`).toDateString()}
                onDelete={canManage ? () => setHolidays(holidays.filter((h) => h !== d)) : undefined} />
            ))}
          </Box>
        )}
        {canManage && (
          <Box display="flex" gap={1.5} alignItems="center">
            <TextField type="date" size="small" label="Date" value={holiday} {...shrink}
              onChange={(e) => setHoliday(e.target.value)} />
            <ButtonOne variant="outlined" startIcon={<Add />} title="Add holiday" disabled={!holiday}
              onClick={() => {
                setHolidays([...new Set([...holidays, holiday])].sort());
                setHoliday("");
              }} />
          </Box>
        )}
      </Section>

      {canManage && (
        <SaveBar busy={busy} title="Save business hours"
          onClick={() => save({ timezone, hours, holidays }, "Business hours saved.")} />
      )}
    </Box>
  );
};

// ── Routing ───────────────────────────────────────────────────────────────────
export const RoutingTab = ({ account, request, notify, onSaved, canManage }) => {
  const settings = account.settings || {};
  const agents = account.agents || [];
  const [agentIds, setAgentIds] = useState(settings.agent_ids || []);
  const [keywords, setKeywords] = useState(settings.handover_keywords ?? ["agent", "human"]);
  const [noMatch, setNoMatch] = useState(settings.handover_on_no_match ?? true);
  const [autoResume, setAutoResume] = useState(settings.auto_resume_hours ?? "");
  const [busy, save] = useSave(account, request, notify, onSaved);

  return (
    <Box display="flex" flexDirection="column" gap={3} maxWidth={860}>
      <Section title="Agents" hint="Chats handed over by the bot are assigned to these people in turn, and they get an email.">
        <Autocomplete
          multiple
          size="small"
          options={agents}
          getOptionLabel={(a) => `${a.name} (${a.role})`}
          value={agents.filter((a) => agentIds.includes(a.id))}
          onChange={(_, value) => setAgentIds(value.map((a) => a.id))}
          renderInput={(params) => <TextField {...params} label="Agents" placeholder="Add agent" {...shrink} />}
          disabled={!canManage}
        />
        {agentIds.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No agents selected: handed-over chats wait unassigned in Conversation › Instagram.
          </Typography>
        )}
      </Section>

      <Section title="When the bot hands over" hint="The bot pauses for that chat so a person can take over.">
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={keywords}
          onChange={(_, value) => setKeywords(value.map((k) => k.trim()).filter(Boolean))}
          renderInput={(params) => (
            <TextField {...params} label="Handover words" placeholder="Type a word and press Enter" {...shrink}
              helperText="If a message contains one of these words, the bot hands over." />
          )}
          disabled={!canManage}
        />
        <SettingRow title="After 2 unanswered messages"
          hint="If no keyword automation matches twice in a row, a person takes over.">
          <ToggleSwitch checked={noMatch} onChange={() => canManage && setNoMatch(!noMatch)} />
        </SettingRow>
      </Section>

      <Section title="Back to the bot" hint="Agents can always click Resume bot in Conversation › Instagram.">
        <SettingRow title="Resume the bot automatically" hint="Turn the bot back on after agents have been quiet for a while.">
          <ToggleSwitch checked={autoResume !== ""} onChange={() => canManage && setAutoResume(autoResume === "" ? 24 : "")} />
        </SettingRow>
        {autoResume !== "" && (
          <HourPresetInput value={autoResume} onChange={setAutoResume} unitLabel="hours without agent activity" />
        )}
      </Section>

      {canManage && (
        <SaveBar
          busy={busy}
          title="Save routing"
          onClick={() =>
            save(
              {
                agent_ids: agentIds,
                handover_keywords: keywords,
                handover_on_no_match: noMatch,
                auto_resume_hours: autoResume === "" ? null : Number(autoResume),
              },
              "Routing saved.",
            )
          }
        />
      )}
    </Box>
  );
};
